// Custom DS-Table column for the Salesperson avatar: mirrors the built-in Table.AvatarColumn, but adds
// `data-private` to the DS Avatar img so LogRocket strips the authenticated profile-picture src from
// recordings (AGENTS.md "Web-front private images" — every avatar backed by profilePicSrc needs it, and
// the built-in AvatarColumn does not expose that hook). Dist-internal imports are the accepted web-front
// convention for custom DS columns — see ContactColumn / StatusPillColumn.
import React from 'react';
import type {ColumnDef} from '@tanstack/react-table';
import {Avatar} from '@forecast-it/design-system';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {defaultColumnDef} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/column/support/BaseColumn';
// eslint-disable-next-line import/no-internal-modules -- established web-front convention for custom DS columns
import {StyledCell, Align} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/table/cell/Cell.styled';
import Styled from 'styled-components';
import type {OpportunitySortingFn, OpportunityTableRow} from '../opportunityTableRows';

export interface SalespersonColumnType {
	id?: string;
	accessorKey: string;
	header: string;
	visible?: boolean;
	align?: Align;
	'data-cy'?: string;
	allowSorting?: boolean;
	sortingFn?: OpportunitySortingFn;
	width?: string;
	// Wraps the rendered value (the call site supplies the EditableCell).
	cellContent: (content: React.ReactNode, row: OpportunityTableRow) => React.ReactNode;
	// Muted label for an opportunity with no salesperson - the cell used to render blank (spec 008 2.1).
	unassignedLabel?: string;
}

const SalespersonLayout = Styled.div`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	max-width: 100%;
`;
const Unassigned = Styled.span`
	color: #71717a;
`;
const SalespersonName = Styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

// Config-only component: DS Table consumes its props via toColumnDef and never renders it.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SalespersonColumn = (_: SalespersonColumnType): null => null;

SalespersonColumn.toColumnDef = (child: React.ReactElement<SalespersonColumnType>): ColumnDef<OpportunityTableRow> => ({
	...defaultColumnDef(child.props),
	accessorKey: child.props.accessorKey,
	sortingFn: child.props.sortingFn,
	cell: ({row}: {row: {original: OpportunityTableRow}}) => {
		const {salespersonName, salespersonImage} = row.original;
		const value = salespersonName ? (
			<SalespersonLayout>
				{/* data-private: profile-picture endpoints are authenticated, so LogRocket must strip the src. */}
				<Avatar size="s" name={salespersonName} src={salespersonImage} data-private />
				<SalespersonName>{salespersonName}</SalespersonName>
			</SalespersonLayout>
		) : (
			<Unassigned>{child.props.unassignedLabel ?? ''}</Unassigned>
		);
		return (
			<StyledCell align={child.props.align} data-cy="opportunity-salesperson-cell">
				{child.props.cellContent(value, row.original)}
			</StyledCell>
		);
	},
});

export default SalespersonColumn;
